/**
 * iframe token 感知 + fetch 请求配置构建（供 fetch / fetchEventSource 使用）。
 */
import { getAuthorization } from '@/utils/utils';
import type { AxiosRequestHeaders } from 'axios';

import {
  appendIframeAuthorizationHeader,
  appendIframeWireBodyFields,
} from './applyTokenPayload';
import { createTokenAwareFunction } from './core';
import type {
  IRequestBuildResult,
  ITokenAwareRequestConfig,
  ITokenData,
} from './types';

/** 获取请求体的独立副本，供后续注入字段使用。 */
const cloneRequestBody = (
  data?: Record<string, unknown>,
): Record<string, unknown> => (data ? { ...data } : {});

/** 为请求头补充本地登录态 Authorization（非 iframe guest 场景）。 */
const applyLocalAuthorization = (
  headers: AxiosRequestHeaders | undefined,
): AxiosRequestHeaders => {
  const nextHeaders: AxiosRequestHeaders = { ...headers };
  const localToken = getAuthorization() || '';
  if (!nextHeaders.Authorization && localToken) {
    nextHeaders.Authorization = localToken;
  }
  return nextHeaders;
};

/** 将 token 感知请求配置组装为 fetch / SSE 可用的请求结果。 */
const buildRequestResult = async (
  requestConfig: ITokenAwareRequestConfig,
): Promise<IRequestBuildResult> => {
  const data = cloneRequestBody(requestConfig.data);
  return {
    url: requestConfig.url,
    method: requestConfig.method || 'POST',
    headers: applyLocalAuthorization(requestConfig.headers) as Record<string, string>,
    body: JSON.stringify(data),
  };
};

/** 将 iframe guest token 与承接页上下文写入 fetch 请求配置。 */
const applyIframeTokenToRequestConfig = (
  tokenData: ITokenData,
  requestConfig: ITokenAwareRequestConfig,
): [ITokenAwareRequestConfig] => {
  const nextData = appendIframeWireBodyFields(
    tokenData,
    cloneRequestBody(requestConfig.data),
  );
  const nextConfig: ITokenAwareRequestConfig = {
    ...requestConfig,
    headers: appendIframeAuthorizationHeader(
      tokenData.token,
      requestConfig.headers,
    ),
    data: nextData,
  };
  return [nextConfig];
};

/** iframe 承接页内构建 fetch / SSE 请求，自动检查刷新 token 并注入鉴权与 wire 字段。 */
export const buildTokenAwareRequest: (
  requestConfig: ITokenAwareRequestConfig,
) => Promise<IRequestBuildResult> = createTokenAwareFunction<
  [ITokenAwareRequestConfig],
  [ITokenAwareRequestConfig],
  IRequestBuildResult
>(buildRequestResult, applyIframeTokenToRequestConfig);
